<template>
  <FinancialsOverviewCard
    title="Balance"
    :loading="current.account.isFetching"
    expandable
  >
    <template #default>
      <div class="d-flex align-center" :class="{ 'flex-column': isMobile }">
        <h4 :class="getBalanceClass">
          {{ formatMoney({ amount: balance, showNegativeSign: false }) }}
        </h4>
        <SoStatus v-if="balance" :status="balanceStatus" class="ml-3" />
      </div>
    </template>
    <template #details>
      <SoSkeleton
        v-if="current.refundEligibility.isFetching"
        type="list-item-three-line"
      />

      <template v-else>
        <div class="d-flex">
          <span>Refunds</span>
          <SoStatus :status="eligibilityStatus" class="ml-3" />
        </div>

        <SoButton
          v-if="isEligibleForRefund"
          title="Create Refund"
          icon="chevron-right"
          class="mt-6 pb-1"
          :disabled="current.refundEligibility.isFetching"
          @click="$router.push({ name: RouteNames.CustomerRefundReview })"
        />

        <SoAlert v-if="pendingRefund" type="info" class="mt-4">
          A refund was requested {{ pendingRefundDateFormatted }}. Account
          balance will be updated soon.
        </SoAlert>

        <SoAlert
          v-else-if="refundEligibilityError"
          v-bind="refundEligibilityError"
          class="mt-4"
        />
      </template>
    </template>
  </FinancialsOverviewCard>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import FinancialsOverviewCard from "../components/FinancialsOverviewCard.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoAlert, { SoAlertProps } from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import { Status } from "@/types/Status"
  import { daysFromTodayFormatted } from "@/helpers/dateHelper"
  import { RouteNames } from "@/types/router"
  import { RefundError } from "@/services-handlers/getRefundEligibility"
  import { REFUND_MIN_AMOUNT_IN_POUNDS } from "@/constants/finance"

  type RefundErrorsMapped = {
    [key in RefundError]: SoAlertProps
  }

  const refundMinAmount = formatMoney({
    amount: REFUND_MIN_AMOUNT_IN_POUNDS,
    round: true,
  })

  const refundErrorsMapped: RefundErrorsMapped = {
    [RefundError.ACCOUNT_TERMINATING]: {
      text: `The customer has not yet received their final bill. Their balance will be settled 10 days after the final bill is issued.`,
      type: "warning",
    },
    [RefundError.CUSTOMER_DECEASED]: {
      text: `We cannot issue a refund until the estate of the deceased has an executor. If there is more information about the executor of the estate, escalate to Bereavement team (COT SCOA).`,
      type: "warning",
    },
    [RefundError.NO_ACTIVE_DIRECT_DEBIT]: {
      text: `We cannot issue a refund without an active DD on the account. Customer can add their payment method in their online account.`,
      type: "warning",
    },
    [RefundError.ACCOUNT_UNDER_REVIEW]: {
      text: `The account is in review, you cannot issue a refund for this customer until their account is resolved.`,
      type: "warning",
    },
    [RefundError.ACCOUNT_IN_DEBIT]: {
      text: `The account balance must be in credit.`,
      type: "error",
    },
    [RefundError.ACCOUNT_BALANCE_LOWER_THAN_MIN_REFUND]: {
      text: `The account balance is lower than the minimum refund amount (${refundMinAmount}). Please confirm this with the customer.`,
      type: "error",
    },
    [RefundError.NO_RECENT_BILLS]: {
      text: `The customer has not recently received a bill and the balance may not be up to date. Any ongoing work on the account will need to be resolved before issuing a refund.`,
      type: "warning",
    },
  }

  export default defineComponent({
    components: {
      FinancialsOverviewCard,
      SoStatus,
      SoSkeleton,
      SoButton,
      SoAlert,
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        formatMoney,
        daysFromTodayFormatted,
        Status,
        RouteNames,
        RefundError,
      }
    },
    computed: {
      balance() {
        return this.current.account.data?.balance ?? 0
      },
      refundEligibility() {
        return this.current.refundEligibility.data
      },
      isEligibleForRefund() {
        return this.refundEligibility.isEligible
      },
      balanceStatus() {
        return this.current.account.data?.balanceStatus
      },
      pendingRefund() {
        return this.refundEligibility.pendingRefund
      },
      pendingRefundDateFormatted() {
        if (!this.pendingRefund) return null
        return daysFromTodayFormatted(this.pendingRefund.createdDttm)
      },
      eligibilityStatus() {
        if (this.pendingRefund) {
          return new Status("default", "Pending")
        }

        if (this.isEligibleForRefund) {
          return new Status("normal", "Eligible")
        }

        return new Status("critical", "Non-eligible")
      },
      refundEligibilityError() {
        if (!this.refundEligibility.error) return null
        return refundErrorsMapped[this.refundEligibility.error]
      },
      getBalanceClass() {
        return {
          "text-danger": this.balance > 0,
          "text-success": this.balance < 0,
          "text-h5": this.isMobile,
          "text-h4": !this.isMobile,
        }
      },
      isMobile() {
        return this.$vuetify.display.mobile
      },
    },
  })
</script>
